<template>
  <AForm
    id="registerForm"
    :localized="false"
    :close-modal-after-submit="false"
    :submit-label="$t('components.aRegisterForm.submitLabel')"
    :schema
    :submit-attrs="{
      block: true,
      color: 'black',
      icon: 'mdi:account-check',
      class:
        'rounded-lg shadow-md dark:shadow-neutral-900/20 shadow-neutral-500/20 text-lg duration-300 py-3',
    }"
    :state="state"
    :handler="
      ({ data }: any) => {
        return $fetch('/api/users', {
          method: 'POST',
          body: toBody(data, true),
        });
      }
    "
    @success="signInHandler"
  >
    <UFormGroup
      size="lg"
      :label="$t('components.aRegisterForm.gender.label')"
      name="gender_id"
    >
      <URadioGroup
        v-model="state.gender_id"
        data-horizontal="1"
        :options="
          genders!.map((gender) => ({ label: gender.name, value: gender.id }))
        "
      />
    </UFormGroup>
    <UFormGroup
      size="lg"
      :label="$t('components.aRegisterForm.firstname.label')"
      name="firstname"
    >
      <UInput
        v-model="state.firstname"
        type="text"
        autocomplete="given-name"
        :placeholder="$t('components.aRegisterForm.firstname.placeholder')"
      />
    </UFormGroup>

    <UFormGroup
      size="lg"
      :label="$t('components.aRegisterForm.lastname.label')"
      name="lastname"
    >
      <UInput
        v-model="state.lastname"
        type="text"
        autocomplete="family-name"
        :placeholder="$t('components.aRegisterForm.lastname.placeholder')"
      />
    </UFormGroup>

    <UFormGroup
      size="lg"
      :label="$t('components.aRegisterForm.email.label')"
      name="email"
    >
      <UInput
        v-model="state.email"
        type="email"
        autocomplete="email"
        placeholder="email@example.com"
        trailing-icon="fluent:mail-32-regular"
      />
    </UFormGroup>

    <UFormGroup
      size="lg"
      :label="$t('components.aRegisterForm.password.label')"
      name="password"
    >
      <UInput
        v-model="state.password"
        type="password"
        autocomplete="new-password"
        placeholder="********************"
        trailing-icon="fluent:lock-closed-key-16-regular"
      />
    </UFormGroup>
  </AForm>
</template>

<script lang="ts" setup>
import type { ARegisterFormProps } from '#common/types'
import { VueReCaptcha } from 'vue-recaptcha-v3'
import { z } from 'zod'

const props = withDefaults(defineProps<ARegisterFormProps>(), {
  signInAttrs: () => ({}),
})
const emit = defineEmits<{
  'logged-in': []
}>()
const { vueApp } = useNuxtApp()
const { t } = useI18n()
const { signIn } = useAuth()
const config = useRuntimeConfig()

const state = ref({
  'email': '',
  'firstname': '',
  'g-recaptcha-response': '' as string | undefined,
  'gender_id': undefined,
  'language_id': undefined,
  'lastname': '',
  'password': '',
})

const { zodPasswordRule } = usePassword()

const schema = z.object({
  email: z.string().email(t('validation.email')),
  firstname: z.string().min(1, { message: t('validation.required') }),
  gender_id: z.number({ message: t('validation.required') }),
  lastname: z.string().min(1, { message: t('validation.required') }),
  password: zodPasswordRule(),
})

const { data: genders, error: gendersError } = await useFetch('/api/genders')
assertDefined(genders, gendersError)

async function signInHandler(data: any) {
  await signIn(
    { password: data.data.password, username: data.data.email },
    { external: true, ...props.signInAttrs },
  )
  emit('logged-in')
}

onMounted(async () => {
  vueApp.use(VueReCaptcha, {
    loaderOptions: {
      autoHideBadge: true,
      renderParameters: {
        hl: 'id',
      },
      useRecaptchaNet: true,
    },
    siteKey: config.public.recaptchaSiteKey as string,
  })
  const { executeRecaptcha } = useGoogleRecaptcha()
  const { token } = await executeRecaptcha('register')
  state.value['g-recaptcha-response'] = token
})
</script>
